/**
 * Vertical card slider.
 * - First item is activated by default on all screen sizes.
 * - On desktop (>= 1200px), hovering activates items; click is blocked.
 * - On mobile, click activates items directly (bypasses plugin slideDown).
 */

const VCS_SECTION = '.e-stn--vertical-card-slider';
const VCS_ACC = '[data-accordion]';
const VCS_ITEM = '.e-acc__itm-wrpr';
const VCS_HEADER = '.e-acc__itm-hdr-wrpr';
const VCS_FLOAT_PANEL = '.e-acc__flt-itm-wrpr';
const VCS_FLOAT_PANELS_WRAPPER = '.e-acc__flt-itms-wrpr';
const VCS_ACTIVE = 'active';
const VCS_DESKTOP_BP = 1200;
const VCS_MIN_HEIGHT = 500;

const vcsIsDesktop = () => window.innerWidth >= VCS_DESKTOP_BP;

/** Set float panel container height to max of all panel content heights. */
const vcsSyncFloatPanelHeights = ( section ) => {
  if ( ! vcsIsDesktop() ) return;

  const acc = section.querySelector( VCS_ACC );
  if ( ! acc ) return;

  const wrapper = acc.querySelector( VCS_FLOAT_PANELS_WRAPPER );
  if ( ! wrapper ) return;

  const panels = acc.querySelectorAll( VCS_FLOAT_PANEL );
  if ( ! panels.length ) return;

  wrapper.style.height = '';

  let maxH = VCS_MIN_HEIGHT;
  panels.forEach( ( panel ) => {
    const card = panel.querySelector( '.e-acc__flt-itm' );
    if ( ! card ) return;

    const savedPanel = {
      position: panel.style.position,
      visibility: panel.style.visibility,
    };
    const savedCard = {
      height: card.style.height,
      minHeight: card.style.minHeight,
    };

    panel.style.position = 'relative';
    panel.style.visibility = 'hidden';
    card.style.height = 'auto';
    card.style.minHeight = `${ VCS_MIN_HEIGHT }px`;

    const h = card.scrollHeight;

    panel.style.position = savedPanel.position;
    panel.style.visibility = savedPanel.visibility;
    card.style.height = savedCard.height;
    card.style.minHeight = savedCard.minHeight;

    if ( h > maxH ) maxH = h;
  } );

  wrapper.style.height = `${ maxH }px`;
  wrapper.style.minHeight = `${ maxH }px`;
};

const vcsDeactivateItems = ( acc ) => {
  acc.querySelectorAll( VCS_ITEM ).forEach( ( el ) => {
    el.classList.remove( VCS_ACTIVE );
    const body = document.getElementById( el.dataset.itemId );
    if ( body ) {
      body.style.display = 'none';
    }
  } );
};

const vcsUpdateFloatingPanels = ( acc, itemId ) => {
  acc.querySelectorAll( VCS_FLOAT_PANEL ).forEach( ( panel ) => {
    const isTarget = panel.dataset.id === itemId;
    panel.style.transition = 'none';
    panel.classList.toggle( VCS_ACTIVE, isTarget );
    panel.style.opacity = isTarget ? '1' : '0';
  } );
};

const vcsActivateItem = ( acc, itemId ) => {
  vcsDeactivateItems( acc );

  const target = acc.querySelector( `${ VCS_ITEM }[data-item-id="${ itemId }"]` );
  if ( target ) {
    target.classList.add( VCS_ACTIVE );
    const body = document.getElementById( itemId );
    if ( body ) {
      body.style.display = 'block';
      body.style.boxSizing = 'border-box';
    }
  }

  vcsUpdateFloatingPanels( acc, itemId );
  vcsSyncFloatPanelHeights( acc.closest( VCS_SECTION ) );
};

const vcsHandleHover = ( e, acc ) => {
  if ( ! vcsIsDesktop() ) return;

  const header = e.target.closest( VCS_HEADER );
  if ( ! header ) return;

  const item = header.closest( VCS_ITEM );
  if ( ! item || item.classList.contains( VCS_ACTIVE ) ) return;

  vcsActivateItem( acc, item.dataset.itemId );
};

const vcsHandleClick = ( e, acc ) => {
  const header = e.target.closest( VCS_HEADER );
  if ( ! header ) return;

  e.stopPropagation();
  e.preventDefault();

  if ( vcsIsDesktop() ) return; // desktop uses hover, just block click

  const item = header.closest( VCS_ITEM );
  if ( ! item || item.classList.contains( VCS_ACTIVE ) ) return;

  vcsActivateItem( acc, item.dataset.itemId );
};

/** On desktop, force default cursor on accordion. */
const vcsApplyHeaderCursor = ( section ) => {
  const acc = section.querySelector( VCS_ACC );
  if ( ! acc ) return;

  const headers = acc.querySelectorAll( `${ VCS_HEADER }, .e-acc__itm-hdr` );
  const value = vcsIsDesktop() ? 'default' : '';
  headers.forEach( ( el ) => {
    el.style.cursor = value;
  } );
};

const vcsAttachHoverBehaviour = ( section ) => {
  const acc = section.querySelector( VCS_ACC );
  if ( ! acc ) return;

  section.addEventListener( 'mouseover', ( e ) => vcsHandleHover( e, acc ), true );
  section.addEventListener( 'click', ( e ) => vcsHandleClick( e, acc ), true );

  vcsSyncFloatPanelHeights( section );
  vcsApplyHeaderCursor( section );
};

const vcsInit = () => {
  if ( document.body.classList.contains( 'wp-admin' ) ) return;

  document.querySelectorAll( VCS_SECTION ).forEach( ( section ) => {
    vcsAttachHoverBehaviour( section );
  } );

  window.addEventListener( 'resize', () => {
    document.querySelectorAll( VCS_SECTION ).forEach( ( section ) => {
      vcsSyncFloatPanelHeights( section );
      vcsApplyHeaderCursor( section );
    } );
  } );
};

const vcsRunInit = () => setTimeout( vcsInit, 100 );

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', vcsRunInit );
} else {
  vcsRunInit();
}
